export interface AccountApiErrorContext {
  endpoint: string;
  httpStatus?: number;
  serverCode?: string;
  cause?: unknown;
}

export class AccountApiError extends Error {
  readonly code: string;
  readonly endpoint: string;
  readonly httpStatus?: number;
  readonly serverCode?: string;
  readonly cause?: unknown;

  constructor(code: string, message: string, context: AccountApiErrorContext) {
    super(message);
    this.name = "AccountApiError";
    this.code = code;
    this.endpoint = context.endpoint;
    this.httpStatus = context.httpStatus;
    this.serverCode = context.serverCode;
    this.cause = context.cause;
  }
}

interface RequestJsonOptions<TError extends AccountApiError> {
  endpoint: string;
  init?: RequestInit;
  unavailableCode: string;
  httpCode: string;
  invalidCode: string;
  createError?: (
    code: string,
    message: string,
    context: AccountApiErrorContext
  ) => TError;
}

interface ErrorEnvelope {
  error: {
    code: string;
    message: string;
  };
}

export async function requestAccountJson<TError extends AccountApiError = AccountApiError>({
  endpoint,
  init,
  unavailableCode,
  httpCode,
  invalidCode,
  createError = (code, message, context) =>
    new AccountApiError(code, message, context) as TError
}: RequestJsonOptions<TError>): Promise<unknown> {
  let response: Response;
  try {
    response = await fetch(endpoint, init);
  } catch (cause) {
    throw createError(unavailableCode, `Unable to request ${endpoint}`, { endpoint, cause });
  }

  let payload: unknown;
  let parseError: unknown;
  try {
    payload = await response.json();
  } catch (cause) {
    parseError = cause;
  }

  if (!response.ok) {
    const envelope = parseErrorEnvelope(payload);
    throw createError(
      httpCode,
      envelope?.error.message ?? `${endpoint} returned HTTP ${response.status}`,
      {
        endpoint,
        httpStatus: response.status,
        serverCode: envelope?.error.code,
        cause: parseError
      }
    );
  }

  if (parseError !== undefined) {
    throw createError(invalidCode, `Invalid JSON returned by ${endpoint}`, {
      endpoint,
      cause: parseError
    });
  }
  return payload;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseErrorEnvelope(value: unknown): ErrorEnvelope | null {
  if (!isRecord(value) || !isRecord(value.error)) return null;
  const { code, message } = value.error;
  return typeof code === "string" && code !== "" && typeof message === "string" && message !== ""
    ? { error: { code, message } }
    : null;
}
